import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import { useTheme } from '@material-ui/core/styles';
import Container from 'common/Container';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';
import handleOpenWhatsApp from 'services/whatsappService';
import { enProduccionConMemForegroundOnPage } from './enProduccionConMemReadableText';
import { EN_PRODUCCION_CON_MEM_LOGO_SRC } from './brandAssets';
import { ROLDA_MEM_ONLY_PROPOSAL } from './roldaMemOnlyProposalContent';

const LandingRoldaMemOnlyEnProduccionConMem = ({ themeMode = 'light' }) => {
  const theme = useTheme();
  const headingColor = enProduccionConMemForegroundOnPage(theme);
  const accent = theme.palette.secondary.main;
  const p = ROLDA_MEM_ONLY_PROPOSAL;

  return (
    <Box sx={{ pb: 8, pt: { xs: 2, md: 3 } }}>
      <Container>
        <Box sx={{ mb: 3 }}>
          <Link
            href="/enproduccionconmem"
            underline="hover"
            sx={{ color: headingColor, fontWeight: 600, '&:hover': { color: accent } }}
          >
            ← Media kit En Producción con MEM
          </Link>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 3,
            mb: 3,
          }}
        >
          <Box
            component="img"
            src={EN_PRODUCCION_CON_MEM_LOGO_SRC}
            alt="En Producción con MEM"
            sx={{
              maxWidth: 220,
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : '#fff',
              p: 1.5,
              borderRadius: 1,
              border: `2px solid ${accent}`,
            }}
          />
          <Box>
            <Typography variant="overline" sx={{ letterSpacing: 1.2, color: accent, fontWeight: 700 }}>
              {p.eyebrow}
            </Typography>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontWeight: 700,
                color: headingColor,
                lineHeight: 1.2,
                mb: 1,
              }}
            >
              {p.title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontStyle: 'italic',
                color: headingColor,
                fontWeight: 600,
              }}
            >
              {p.concept}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1.5, maxWidth: 720, lineHeight: 1.65 }}>
              {p.tagline}
            </Typography>
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 4,
            border: `1px solid ${theme.palette.divider}`,
            borderLeft: `4px solid ${accent}`,
            bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
          }}
        >
          <Grid container spacing={2} alignItems="baseline">
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700, letterSpacing: 0.08 }}>
                {p.budgetLabel}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: accent, mt: 0.5 }}>
                {p.budgetAmount}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700, letterSpacing: 0.08 }}>
                {p.programLabel}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: headingColor, mt: 0.5 }}>
                {p.programName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700, letterSpacing: 0.08 }}>
                {p.focusLabel}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: headingColor, mt: 0.5, lineHeight: 1.5 }}>
                {p.focusText}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        <PriceVatDisclaimer sx={{ mb: 3 }} />

        <Typography variant="body1" color="textSecondary" sx={{ mb: 4, maxWidth: 800, lineHeight: 1.75 }}>
          {p.intro}
        </Typography>

        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontWeight: 700,
            color: headingColor,
            mb: 2,
          }}
        >
          Qué incluye
        </Typography>
        <Grid container spacing={2.5} sx={{ mb: 5 }}>
          {p.deliverables.map((d) => (
            <Grid item xs={12} md={p.deliverables.length >= 4 ? 3 : 4} key={d.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  height: '100%',
                  border: `1px solid ${theme.palette.divider}`,
                  borderTop: `4px solid ${accent}`,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800, color: headingColor, mb: 1.5, lineHeight: 1.3 }}>
                  {d.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.7 }}>
                  {d.body}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 4, borderColor: accent, opacity: 0.35 }} />

        <Typography
          variant="h4"
          component="h2"
          sx={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontWeight: 700,
            color: headingColor,
            mb: 2,
          }}
        >
          {p.whyTitle}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 5 }}>
          {p.whyPoints.map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: accent, mb: 1 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.75 }}>
                  {item.body}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <PriceVatDisclaimer sx={{ mb: 3 }} />

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            textAlign: 'center',
            bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: headingColor, mb: 2 }}>
            ¿Activamos El Momento Rolda en En Producción con MEM?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, maxWidth: 560, mx: 'auto' }}>
            Referencia: programa, cintillo en vivo y logo permanente en el mini site En Producción con MEM en TNO Network ($100/mes). Contacto comercial TNO Network.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" color="secondary" size="large" onClick={() => handleOpenWhatsApp()}>
              WhatsApp comercial
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              component={Link}
              href="mailto:mercadeoyventas@tnonetwork.com?subject=Propuesta%20Rolda%20%E2%80%94%20El%20Momento%20Rolda%20%28solo%20MEM%29"
            >
              mercadeoyventas@tnonetwork.com
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

LandingRoldaMemOnlyEnProduccionConMem.propTypes = {
  themeMode: PropTypes.string,
};

export default LandingRoldaMemOnlyEnProduccionConMem;
